import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  SimpleChanges
} from '@angular/core';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { DataAnalyticsTaggingConstants, DataAnalyticsTaggingEvent, ToastMessageService } from '@tdwb/core';
import { LearnRestService } from '@tdwb/webbroker/core';
import { Lesson } from '../domain/learn.domain';

@Component({
  selector: 'td-wb-learn-lesson-content',
  templateUrl: 'learn-lesson-content.component.html',
  styleUrls: ['learn-lesson-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LearnLessonContentComponent implements OnChanges, OnInit, OnDestroy {
  @Input() id: string;
  @Input() loadingIndicator: boolean = true;
  @Input() section: string;
  @Output() lessonLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  lesson: Lesson;
  likeDisabled: boolean = false;
  saveDisabled: boolean = false;
  eventData: DataAnalyticsTaggingEvent = {};

  private readonly destroy: Subject<void> = new Subject<void>();

  constructor(private learnRestService: LearnRestService,
              private toastMessageService: ToastMessageService,
              private changeDetectorRef: ChangeDetectorRef,
              private location: Location) {
  }

  ngOnInit(): void {
    if (this.id !== undefined) {
      this.retrieveLessonDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.id !== undefined
      && changes.id.previousValue !== undefined
      && changes.id.previousValue !== changes.id.currentValue) {
      this.retrieveLessonDetails();
    }
  }

  retrieveLessonDetails(): void {
    this.learnRestService.retrieveLesson(this.id)
      .pipe(
        takeUntil(this.destroy),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (lesson: Lesson): void => {
          this.lesson = lesson;
          this.loadingIndicator = false;
          this.lessonLoaded.emit(true);
        },
        error: (): void => {
          this.loadingIndicator = false;
          this.lessonLoaded.emit(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  getLike(): boolean {
    return this.lesson.like === 5;
  }

  getLikeCount(): number {
    return this.lesson.likeCount === 0 ? undefined : this.lesson.likeCount;
  }

  toggleLike(): void {
    const resetLike = this.lesson.like;
    const like = this.lesson.like === 5 ? 0 : 5;
    this.lesson.like = like;
    //TODO: create enum?
    const action = like === 0 ? 'unliked' : 'liked';
    this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:learn:lesson:${this.section}:${action}:${this.lesson.title}`);
    this.likeDisabled = true;
    this.learnRestService
      .postLike({
        id: this.lesson.contentMetaData.id,
        like
      })
      .pipe(
        takeUntil(this.destroy),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (count: number): void => {
          this.lesson.likeCount = count;
          this.likeDisabled = false;
        },
        error: (): void => {
          this.lesson.like = resetLike;
          this.likeDisabled = false;
        }
      });
  }

  toggleSave(): void {
    this.saveDisabled = true;
    this.learnRestService.toggleVideoBookMark(this.lesson)
      .pipe(
        takeUntil(this.destroy),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (): void => {
          this.lesson.saved = !this.lesson.saved;
          //TODO: create enum?
          const action = this.lesson.saved ? 'saved' : 'unsaved';
          this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:learn:${this.section}:${action}:${this.lesson.title}`);
          this.saveDisabled = false;
          const messageKey = this.lesson.saved ? 'tdWb.learn.notification.SAVED' : 'tdWb.learn.notification.UNSAVED';
          this.toastMessageService.setToast(messageKey);
        },
        error: (): void => {
          this.saveDisabled = false;
        }
      });
  }

  back(): void {
    this.location.back();
  }
}
